import Vue from "vue";
import { savedFiltersApi } from "@/services/layers/savedFiltersApi";
import { hasAppliedPredicate, migrateAppliedFilter } from "@/services/layers/advancedFilters";

// Filtros guardados por capa (personales del usuario). Sólo memoria — igual
// que store/modules/symbology.js (D8): se recargan desde el backend cada vez
// que se abre un mapa, mueren en refresh. A diferencia de Simbología, el
// filtro *activo* de una capa sigue viviendo en sessionStorage
// (attributeFilterState.js) — este módulo es la BIBLIOTECA de presets con
// nombre desde la que se puede cargar ese filtro activo, no el filtro activo
// en sí.
const normalizePreset = preset => ({ ...preset, filter: migrateAppliedFilter(preset.filter) });

export default {
  namespaced: true,
  state: {
    byLayer: {},        // { [layerId]: [{ id, id_layer, name, filter, created_at, updated_at }] }
    incompatible: {},   // { [presetId]: "razón" } — marcado en la sesión al fallar la carga (ver AttributesTable.vue)
  },
  getters: {
    forLayer: state => layerId => state.byLayer[layerId] || [],
    isIncompatible: state => presetId => !!state.incompatible[presetId],
    reason: state => presetId => state.incompatible[presetId] || "",
  },
  mutations: {
    setAll(state, byLayer) {
      state.byLayer = byLayer;
    },
    upsert(state, { layerId, preset }) {
      const list = (state.byLayer[layerId] || []).filter(p => p.id !== preset.id);
      list.push(preset);
      Vue.set(state.byLayer, layerId, list);
    },
    remove(state, { layerId, id }) {
      Vue.set(state.byLayer, layerId, (state.byLayer[layerId] || []).filter(p => p.id !== id));
    },
    markIncompatible(state, { id, reason }) {
      Vue.set(state.incompatible, id, reason);
    },
    // Llamado al cerrar sesión (Logout.vue) — sin esto, la caché en memoria
    // de filtros guardados del usuario saliente sigue disponible para
    // getters/componentes hasta que algo vuelva a despachar loadForLayer(s).
    reset(state) {
      state.byLayer = {};
      state.incompatible = {};
    },
  },
  actions: {
    // Carga masiva para todas las capas visibles de un mapa (VisorViewer.vue).
    // Fallo silencioso a propósito: si el backend no responde, el visor queda
    // sin selects de filtros guardados, no roto (mismo criterio que
    // restSymbology.js::loadSymbologiesForLayers).
    async loadForLayers({ commit }, layerIds) {
      try {
        const response = await savedFiltersApi.listForLayers(layerIds || []);
        const byLayer = {};
        (response.data || []).forEach(item => {
          const preset = normalizePreset(item);
          if (!hasAppliedPredicate(preset.filter)) return; // un preset sin predicado es ruido no cargable
          (byLayer[item.id_layer] = byLayer[item.id_layer] || []).push(preset);
        });
        commit("setAll", byLayer);
      } catch (error) {
        console.log("saved-filters:", error);
      }
    },
    // Carga perezosa de una sola capa — usado para capas importadas, que no
    // participan de la carga masiva de VisorViewer.vue (sus ids no forman
    // parte del catálogo del mapa).
    async loadForLayer({ commit }, layerId) {
      try {
        const response = await savedFiltersApi.listForLayers([layerId]);
        (response.data || []).forEach(item => {
          const preset = normalizePreset(item);
          if (hasAppliedPredicate(preset.filter)) commit("upsert", { layerId, preset });
        });
      } catch (error) {
        console.log("saved-filters:", error);
      }
    },
    // create/rename/overwrite/remove NO atrapan errores: SavedFiltersModal.vue
    // necesita distinguir el 409 de nombre duplicado del resto para ofrecer
    // "actualizar" en vez de un toast genérico.
    async create({ commit }, { layerId, name, filter }) {
      const response = await savedFiltersApi.create({ layerId, name, filter });
      const preset = normalizePreset(response.data);
      commit("upsert", { layerId, preset });
      return preset;
    },
    // El backend responde solo {message} en PUT (no el documento completo,
    // ver routes/saved_filter.go::UpdateSavedFilter) — el preset actualizado
    // se arma localmente a partir del que ya se tenía.
    async rename({ commit }, { layerId, preset, name }) {
      await savedFiltersApi.update(preset.id, { name });
      const updated = { ...preset, name };
      commit("upsert", { layerId, preset: updated });
      return updated;
    },
    async overwrite({ commit }, { layerId, preset, filter }) {
      await savedFiltersApi.update(preset.id, { filter });
      const updated = { ...preset, filter: migrateAppliedFilter(filter) };
      commit("upsert", { layerId, preset: updated });
      return updated;
    },
    async remove({ commit }, { layerId, id }) {
      await savedFiltersApi.remove(id);
      commit("remove", { layerId, id });
    },
  },
};
